import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { IntlShape, injectIntl, FormattedMessage } from 'react-intl';
import Axios from 'axios';

import intlShape from '@/shared/utils/intlShape';
import AuthService, { functionalities, privileges } from '@/shared/services/AuthService';
import { CanCreate } from '@/shared/components/security';
import { getInvoiceStreamNames } from '@/shared/services/InvoiceStreamService';
import ToastrService from '@/shared/services/ToastrService';
import messages from './registrationMessages';
import RelationTagsOnRegistration from './RelationTagsOnRegistration';
import TimeInput from '../input/TimeInput';
import Checkbox from '../input/Checkbox';
import guid from '../../utils/guid';
import {
  actions,
  registrationShape,
  defaultOptions,
  getAssignmentTemplateId,
  getFirstSelectedInvoiceStreamId,
} from './registrationHelpers';
import {
  FormGroupPart,
  UnitPricePart,
  DiscountPart,
  TravelQuantityPart,
  CostQuantityPart,
  DurationFormGroup,
  VatPercentagePart,
  TotalAmountPart,
  RelationFormGroup,
  InteractionFormGroup,
  AssignmentFormGroup,
  TaskFormGroup,
  OwnCompanyRelationFormGroup,
  DurationDaysFormGroup,
  ResourceTypeRadioButtons,
  DatePart,
  ReferenceYearPart,
  DurationTypeFormGroup,
  HalfDayFormGroup,
} from './partials';
import {
  retrieveResources,
  getInForfaitAndPrice,
  getLastRegistrationTime,
  getInteractionForRegistration,
  getPrice,
  getAbsenceBalance,
} from './resourceApi';
import api from '@/shared/api';
import config from '@/config';
import ResourceAutoComplete, { resourceFilter } from './ResourceAutoComplete';
import AbsenceBalanceFormGroup from './partials/AbsenceBalanceFormGroup';
import UserFormGroup from './partials/UserFormGroup';
import moment from 'moment';

class RegistrationComponent extends Component {
  hasPriceListPermission = AuthService.hasPermission({
    functionality: functionalities.Pricelist,
    privilege: 'Read',
  });

  state = {
    resources: null,
    trainings: [],
    selectedTraining: null,
    invoiceStreams: null,
  };

  setPriceLoading = loading => {
    const { onPriceLoadingChanged } = this.props;
    if (onPriceLoadingChanged) onPriceLoadingChanged(loading);
  };

  componentDidMount() {
    const { registration, options: externalOptions } = this.props;
    const options = { ...defaultOptions, ...(externalOptions || {}) };

    this.retrieveResources(registration.type);
    // Only new registrations validate the preset relation; existing ones keep their data
    this.loadInvoiceStreams(registration.relation?.id, { warnWhenEmpty: !registration.id });

    if (registration.type === 5) {
      this.retrieveTrainings();
    }

    if (!registration.id) {
      // New registration
      if (
        (registration.type === 0 ||
          registration.type === 3 ||
          registration.type === 4 ||
          registration.type === 5) &&
        registration.date &&
        options.showFromUntil
      ) {
        getLastRegistrationTime(registration.date).then(value => {
          this.doAction(actions.fromMinutesChanged, value);
        });
      }
      if (options.defaultDuration > 0) {
        this.doAction(actions.durationMinutesChanged, options.defaultDuration);
      }
    } else if (
      registration.group &&
      options.showInteraction &&
      AuthService.hasPermission({
        functionality: 'InteractionsOwn',
        privilege: privileges.Read,
      })
    ) {
      getInteractionForRegistration(registration.group).then(interaction => {
        this.doAction(actions.interactionInit, interaction);
      });
    }
  }
  componentDidUpdate({ registration: oldRegistration }) {
    const { registration } = this.props;
    if (registration.type !== oldRegistration.type) {
      this.retrieveResources(registration.type);
      this.retrieveTrainings();
    }
    if (registration.relation?.id !== oldRegistration.relation?.id) {
      this.loadInvoiceStreams(registration.relation?.id, {
        updatePrice: true,
        warnWhenEmpty: true,
      });
    }
  }

  loadInvoiceStreams = (relationId, { updatePrice = false, warnWhenEmpty = false } = {}) => {
    this.setState({ invoiceStreams: null });
    if (!relationId) return;
    getInvoiceStreamNames(relationId).then(invoiceStreams => {
      const { registration, intl } = this.props;
      // Ignore stale responses when the relation changed again in the meantime
      if (registration.relation?.id !== relationId) return;
      if (warnWhenEmpty && !invoiceStreams.length) {
        // A relation without invoice streams cannot be registered on, so deselect it again
        ToastrService.warning(
          intl.formatMessage(messages.YouCannotSelectThisRelationBecauseItHasNoInvoiceStreams),
        );
        this.doAction(actions.relationChanged, null);
        return;
      }
      this.setState({ invoiceStreams });
      this.doAction(actions.invoiceStreamsLoaded, invoiceStreams);

      if (!updatePrice || !this.hasPriceListPermission) return;
      const provisionalId = registration.relation?.invoiceStreamId ?? null;
      const invoiceStreamId =
        !registration.invoiceStreamId || registration.invoiceStreamId === provisionalId
          ? getFirstSelectedInvoiceStreamId(invoiceStreams, provisionalId)
          : registration.invoiceStreamId;
      if (!invoiceStreamId) return;
      if (registration.resource && registration.resource.id) {
        this.setPriceLoading(true);
        getInForfaitAndPrice(invoiceStreamId, registration.resource, registration.userId)
          .then(priceInfo => {
            this.doAction(actions.priceInfoChanged, priceInfo);
          })
          .finally(() => this.setPriceLoading(false));
      }
    });
  };

  retrieveResources = (resourceType, afterLoadCallback) => {
    const { intl } = this.props;
    this.setState({ resources: null });
    retrieveResources(intl, resourceType).then(resources => {
      this.setState({ resources }, () => {
        if (afterLoadCallback) afterLoadCallback(resources);
      });

      const { registration, defaultResourceId } = this.props;
      if (registration.id && registration.resource && registration.resource.id) {
        // Retrieve price options when editing registration
        if (!this.hasPriceListPermission) return;
        const { invoiceStreams } = this.state;
        const invoiceStreamId =
          registration.invoiceStreamId ||
          getFirstSelectedInvoiceStreamId(invoiceStreams, registration.relation?.invoiceStreamId) ||
          null;
        if (invoiceStreamId) {
          this.setPriceLoading(true);
          getPrice(invoiceStreamId, registration.resource.categoryId, registration.userId)
            .then(({ data }) => {
              this.doAction(actions.setPriceOptions, data ? [data] : []);
            })
            .finally(() => this.setPriceLoading(false));
        }
        return;
      }
      const defaultResource =
        !registration.resource && defaultResourceId
          ? resources.find(
              x =>
                x.id === defaultResourceId &&
                resourceFilter(
                  x,
                  {
                    crmType: registration?.relation?.crm,
                    assignmentTemplateId: registration?.assignmentTemplateId,
                  },
                  !!registration.id,
                ),
            )
          : null;

      const onlyResource = !registration.resource && resources.length === 1 ? resources[0] : null;
      const resourceById =
        !registration.resource && registration.resourceId
          ? resources.find(x => x.id === registration.resourceId)
          : null;

      // Pick in priority: resourceId match > default > only one
      const selected = registration.resource || resourceById || defaultResource || onlyResource;

      if (selected) {
        this.onResourceChanged(selected);
      }
    });
  };

  retrieveTrainings = () => {
    const { intl, registration } = this.props;
    const userId = registration.userId || AuthService.getUserId();
    const trainingId = registration?.trainingId || null;

    this.setState({ trainings: null });

    const params = new URLSearchParams({ userId });
    if (trainingId) {
      params.append('trainingId', trainingId);
    }

    api.base
      .get(`${config.appUrls.api}/api/TrainingEmployee/activeTrainingsByUserId?${params}`)
      .then(({ data: trainings }) => {
        this.setState({ trainings });
        if (registration?.trainingId) {
          const selectedTraining = trainings.find(
            t => String(t.trainingId) === registration.trainingId,
          );
          if (selectedTraining) {
            this.onTrainingChanged(selectedTraining.trainingId);
            if (selectedTraining.trainingEmployeeStatus === 2) {
              this.doAction(actions.trainingCompletedChanged, true);
            }
          }
        }
      })
      .catch(error => {
        if (!Axios.isCancel(error)) return error;
      });
  };

  onDateChanged = date => {
    this.doAction(actions.dateChanged, date);
    const { registration } = this.props;
    if (
      registration.type === 0 ||
      registration.type === 3 ||
      registration.type === 4 ||
      registration.type === 5
    ) {
      getLastRegistrationTime(date).then(fromMinutes => {
        if (fromMinutes) {
          this.doAction(actions.fromMinutesChanged, fromMinutes);
        }
      });
    }
  };
  onRelationChanged = relation => {
    // componentDidUpdate picks up the relation change, loads its invoice streams,
    // and updates the price info once they arrive
    this.doAction(actions.relationChanged, relation);
  };
  onAssignmentChanged = invoiceStreamId => {
    const { invoiceStreams } = this.state;
    this.doAction(actions.assignmentChanged, {
      invoiceStreamId,
      assignmentTemplateId: getAssignmentTemplateId(invoiceStreams, invoiceStreamId),
    });
    if (!this.hasPriceListPermission || !invoiceStreamId) return;
    const { registration } = this.props;
    if (registration.resource && registration.resource.id) {
      this.setPriceLoading(true);
      getInForfaitAndPrice(invoiceStreamId, registration.resource, registration.userId)
        .then(priceInfo => {
          this.doAction(actions.priceInfoChanged, priceInfo);
        })
        .finally(() => this.setPriceLoading(false));
    }
  };

  getResourceChangedObject = resource => {
    const { registration } = this.props;
    if (registration.type === 3 || registration.type === 4) {
      return getAbsenceBalance(resource, registration.userId, registration.date).then(balance => ({
        ...balance,
        resource,
        inForfait: null,
        priceOptions: null,
        price: null,
      }));
    }
    if (this.hasPriceListPermission) {
      const { invoiceStreams } = this.state;
      const invoiceStreamId =
        registration.invoiceStreamId ||
        getFirstSelectedInvoiceStreamId(invoiceStreams, registration.relation?.invoiceStreamId) ||
        null;
      if (resource && resource.id && invoiceStreamId) {
        this.setPriceLoading(true);
        return getInForfaitAndPrice(invoiceStreamId, resource, registration.userId)
          .then(priceInfo => {
            const value = { ...priceInfo, resource };
            return value;
          })
          .finally(() => this.setPriceLoading(false));
      }
    }
    const value = { resource, inForfait: null, priceOptions: null, price: null };
    return Promise.resolve(value);
  };
  onResourceChanged = resource => {
    this.getResourceChangedObject(resource).then(value =>
      this.doAction(actions.resourceChanged, value),
    );
  };

  onTrainingChanged = trainingId => {
    const { trainings, resources } = this.state;
    if (!trainingId) {
      this.doAction(actions.trainingChanged, null);
      this.doAction(actions.trainingCompletedChanged, false);
      return;
    }
    const selectedTraining = (trainings || []).find(t => t.trainingId === trainingId);
    if (!selectedTraining) return;
    if (!selectedTraining.trainingResourceId) {
      this.doAction(actions.trainingChanged, selectedTraining);
    }
    const resource = resources.find(x => x.id === selectedTraining.trainingResourceId);
    if (resource) {
      return this.getResourceChangedObject(resource).then(updateResource =>
        this.doAction(actions.trainingChanged, { ...selectedTraining, updateResource }),
      );
    }
  };

  onTaskChanged = task => {
    if (!task || !task.id) {
      this.doAction(actions.taskChanged, null);
      return;
    }
    const { resourceId = null } = task;
    const { registration } = this.props;
    if (!!resourceId && registration.type === 0) {
      const { resources } = this.state;
      const resource = resources.find(x => x.id === resourceId);
      if (resource) {
        return this.getResourceChangedObject(resource).then(updateResource =>
          this.doAction(actions.taskChanged, { ...task, updateResource }),
        );
      }
    }
    this.doAction(actions.taskChanged, task);
  };

  doAction = (action, value) => {
    const { onAction } = this.props;
    onAction(action, value);
  };

  render() {
    const { resources, trainings, invoiceStreams } = this.state;
    const {
      registration,
      intl,
      disabled,
      options: externalOptions,
      showValidation,
      mainTaskId,
      overtimeRemaining,
    } = this.props;

    const invalidResource = !registration.resource || !registration.resource.id;

    const options = { ...defaultOptions, ...(externalOptions || {}) };

    const isTime =
      registration.type === 0 ||
      registration.type === 3 ||
      registration.type === 4 ||
      registration.type === 5;
    const showPriceInfo =
      registration.type !== 3 &&
      registration.type !== 4 &&
      registration.type !== 5 &&
      this.hasPriceListPermission;
    const showAssignments =
      options.showAssignments && registration.relation && (invoiceStreams || []).length > 1;
    const showTasks =
      options.showTasks &&
      !!registration.invoiceStreamId &&
      registration.invoiceStreamId !== guid.empty;

    const focusRelationInput =
      options.showRelation && !!options.editableRelation && !registration.relation;

    const showDurationInDays =
      (registration.type === 3 || registration.type === 4 || registration.type === 5) &&
      !registration.id;

    const showOwnCompanyRelation =
      options.ownCompanyRelation ||
      registration.type === 3 ||
      registration.type === 4 ||
      registration.type === 5;
    const showTraining = registration.type === 5;

    if (!AuthService.hasFeature('ReferenceYear')) {
      options.showReferenceYear = false;
    }

    return (
      <>
        {options.showOtherUsers &&
          !!registration.userId &&
          registration.userId !== AuthService.getUserId() && (
            <UserFormGroup userId={registration.userId} />
          )}

        {options.showRelation && !!showOwnCompanyRelation && (
          <OwnCompanyRelationFormGroup
            relation={registration.relation}
            readOnly={!options.editableRelation}
            disabled={disabled}
            autoFocus={focusRelationInput}
            onChange={this.onRelationChanged}
            userId={registration.userId}
          />
        )}
        {options.showDate && (
          <div className="form-group">
            <DatePart
              value={registration.date}
              onChange={this.onDateChanged}
              disabled={disabled}
              showValidation={showValidation}
              inputCols="col-sm-10"
              intl={intl}
            />
          </div>
        )}
        {options.showRelation && !showOwnCompanyRelation && (
          <RelationFormGroup
            relation={registration.relation}
            readOnly={!options.editableRelation}
            disabled={disabled}
            onChange={this.onRelationChanged}
            autoFocus={focusRelationInput}
            showValidation={showValidation}
            crmTypes={options.crmTypes}
            // A relation that has ended can still be booked on for dates before it ended.
            activeOn={registration.date}
          />
        )}
        {showAssignments ? (
          <AssignmentFormGroup
            invoiceStreams={invoiceStreams || []}
            readOnly={!options.editableRelation}
            value={registration.invoiceStreamId}
            disabled={disabled}
            onChange={this.onAssignmentChanged}
            intl={intl}
          />
        ) : null}
        {showTasks ? (
          <TaskFormGroup
            plannedTaskId={registration.plannedTaskId}
            invoiceStreamId={registration.invoiceStreamId}
            onChange={this.onTaskChanged}
            mainTaskId={mainTaskId}
            disabled={disabled}
            showValidation={showValidation}
          />
        ) : null}
        {options.showReferenceYear && registration.relation && (
          <div className="form-group">
            <ReferenceYearPart
              value={registration.referenceYear}
              onChange={value => this.doAction(actions.referenceYearChanged, value)}
              disabled={disabled}
              showValidation={showValidation}
              inputCols="col-sm-10"
              intl={intl}
            />
          </div>
        )}
        {options.showRelation && (
          <RelationTagsOnRegistration
            key="registrationRelationTags"
            relationId={registration.relation?.id}
          />
        )}
        {options.resourceTypes && options.resourceTypes.length > 1 ? (
          <div className="form-group">
            <FormGroupPart inputCols="col-sm-10">
              <ResourceTypeRadioButtons
                value={registration.type}
                types={options.resourceTypes}
                disabled={disabled}
                onChange={value => this.doAction(actions.resourceTypeChanged, value)}
              />
            </FormGroupPart>
          </div>
        ) : null}

        {showTraining ? (
          <>
            <div className="form-group">
              <FormGroupPart label={messages.Training} inputCols="col-sm-10">
                {trainings && trainings.length > 0 ? (
                  <select
                    value={registration.trainingId || ''}
                    disabled={options.trainingReadOnly}
                    onChange={e => this.onTrainingChanged(e.target.value)}
                    className="form-control"
                  >
                    {!registration.trainingId && (
                      <option value="">{intl.formatMessage(messages.SelectTraining)}</option>
                    )}
                    {(trainings || []).map(training => (
                      <option key={training.trainingId} value={training.trainingId}>
                        {training.trainingName}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="form-control-static">
                    <FormattedMessage {...messages.NoAssignedTrainings} />
                  </p>
                )}
              </FormGroupPart>
            </div>

            {registration.trainingId && (
              <div className="form-group">
                <div className="col-sm-4 col-sm-offset-2">
                  <Checkbox
                    id="TrainingCompletedField"
                    value={registration.trainingCompleted}
                    text={intl.formatMessage(messages.TrainingComplete)}
                    disabled={disabled}
                    onClick={() =>
                      this.doAction(
                        actions.trainingCompletedChanged,
                        !registration.trainingCompleted,
                      )
                    }
                  />
                </div>
              </div>
            )}
          </>
        ) : null}
        {!showTraining ? (
          <div className="form-group">
            <FormGroupPart
              label={messages.What}
              inputCols="col-sm-10"
              invalid={showValidation && invalidResource}
            >
              {resources && resources.length > 0 ? (
                <ResourceAutoComplete
                  items={resources}
                  autoFocus={!focusRelationInput && !registration.resource}
                  placeholder={intl.formatMessage(messages.SearchPriceListItem)}
                  onChange={this.onResourceChanged}
                  emptyLabel={intl.formatMessage(messages.NoPriceListItemsFound)}
                  value={registration.resource}
                  required
                  disabled={showTraining || disabled}
                  crmType={
                    registration.relation?.crm ??
                    (options.crmTypes && options.crmTypes.length === 1
                      ? options.crmTypes[0]
                      : undefined)
                  }
                  assignmentTemplateId={registration.assignmentTemplateId}
                  intl={intl}
                  isSmall
                />
              ) : (
                <FormattedMessage {...messages.NoPriceListItemsFound} />
              )}
            </FormGroupPart>
          </div>
        ) : null}
        {showDurationInDays && options.showDuration && (
          <DurationTypeFormGroup
            value={registration.durationType}
            disabled={invalidResource || disabled}
            id="DurationTypeField"
            intl={intl}
            onChange={durationType => this.doAction(actions.durationTypeChanged, durationType)}
          />
        )}
        {isTime && options.showFromUntil && registration.durationType === 0 && (
          <div className="form-group">
            <FormGroupPart label={messages.From}>
              <TimeInput
                value={registration.fromMinutes}
                id="FromMinutes"
                disabled={invalidResource || disabled}
                onChange={value => this.doAction(actions.fromMinutesChanged, value)}
              />
            </FormGroupPart>
            <FormGroupPart label={messages.Until} alignLabel="left">
              <TimeInput
                value={registration.toMinutes}
                id="ToMinutes"
                disabled={invalidResource || disabled}
                onChange={value => this.doAction(actions.toMinutesChanged, value)}
              />
            </FormGroupPart>
          </div>
        )}
        {isTime && options.showDuration && registration.durationType === 0 && (
          <DurationFormGroup
            duration={registration.durationMinutes}
            onChange={value => this.doAction(actions.durationMinutesChanged, value)}
            disabled={invalidResource || disabled}
            showValidation={showValidation}
            max={1439 - (registration.fromMinutes || 0)}
            intl={intl}
          />
        )}
        {showDurationInDays && options.showDuration && registration.durationType === 1 && (
          <DurationDaysFormGroup
            duration={registration.durationDays}
            userId={registration.userId}
            date={registration.date}
            resourceType={registration.type}
            onChange={value => this.doAction(actions.durationDaysChanged, value)}
            disabled={invalidResource || disabled}
            showValidation={showValidation}
            intl={intl}
          />
        )}
        {showDurationInDays && options.showDuration && registration.durationType === 2 && (
          <HalfDayFormGroup
            duration={registration.durationDays}
            userId={registration.userId}
            date={registration.date}
            resourceType={registration.type}
            isAfternoon={registration.isAfternoon}
            onChange={value => this.doAction(actions.isAfternoonChanged, value)}
            disabled={invalidResource || disabled}
            showValidation={showValidation}
            intl={intl}
          />
        )}
        {(registration.type === 3 || registration.type === 4) && registration.absenceBalance ? (
          <AbsenceBalanceFormGroup
            absenceBalance={registration.absenceBalance}
            durationType={registration.durationType}
            duration={registration.duration}
            durationDays={registration.durationDays}
            showValidation={showValidation}
            intl={intl}
          />
        ) : null}
        {registration.type === 1 && options.showQuantity && (
          <div className="form-group">
            <CostQuantityPart
              quantity={registration.quantity}
              onChange={value => this.doAction(actions.quantityChanged, value)}
              disabled={invalidResource || disabled}
              showValidation={showValidation}
            />
          </div>
        )}
        {registration.type === 2 && options.showQuantity && (
          <div className="form-group">
            <TravelQuantityPart
              quantity={registration.quantity}
              onChange={value => this.doAction(actions.quantityChanged, value)}
              disabled={invalidResource || disabled}
              intl={intl}
              showValidation={showValidation}
            />
          </div>
        )}
        {showPriceInfo && (options.showPrice || options.showDiscount) && (
          <div className="form-group">
            {options.showPrice && (
              <UnitPricePart
                unitPrice={registration.amount}
                price={registration.price}
                priceOptions={registration.priceOptions}
                isFixedPrice={registration.isFixedPrice}
                intl={intl}
                onUnitPriceChanged={value => this.doAction(actions.unitPriceChanged, value)}
                onPriceChanged={value => this.doAction(actions.priceChanged, value)}
                disabled={invalidResource || disabled}
              />
            )}
            {options.showDiscount && (
              <DiscountPart
                discount={registration.discountPercentage}
                onChange={value => this.doAction(actions.discountChanged, value)}
                disabled={invalidResource || disabled}
              />
            )}
          </div>
        )}
        {showPriceInfo && (options.showVat || options.showTotalAmount) && (
          <div className="form-group">
            {options.showVat && (
              <VatPercentagePart
                vatPercentage={registration.vatPercentage}
                vatType={registration.vatType}
                onChange={value => this.doAction(actions.vatPercentageChanged, value)}
                disabled={invalidResource || disabled}
              />
            )}
            {options.showTotalAmount && (
              <TotalAmountPart
                value={registration.totalAmount}
                isFixedPrice={registration.isFixedPrice}
                intl={intl}
                onChange={value => this.doAction(actions.totalAmountChanged, value)}
                disabled={invalidResource || disabled}
              />
            )}
          </div>
        )}
        {showPriceInfo && (options.showBillable || options.showInForfait) && (
          <div className="form-group">
            {options.showBillable && (
              <div className="col-sm-4 col-sm-offset-2">
                <Checkbox
                  id="IsBillableField"
                  value={registration.isBillable}
                  text={intl.formatMessage(messages.Billable)}
                  disabled={invalidResource || disabled}
                  onClick={() => this.doAction(actions.isBillableChanged, !registration.isBillable)}
                />
              </div>
            )}
            {options.showInForfait &&
              registration.inForfait !== undefined &&
              registration.inForfait !== null && (
                <div className="col-sm-4 col-sm-offset-2">
                  <Checkbox
                    id="InForfaitField"
                    value={registration.inForfait}
                    text={intl.formatMessage(messages.InForfait)}
                    disabled={invalidResource || disabled}
                    onClick={() => this.doAction(actions.inForfaitChanged, !registration.inForfait)}
                  />
                </div>
              )}
          </div>
        )}
        {options.showInvoiceRemark && (
          <div className="form-group">
            <FormGroupPart
              label={
                <span>
                  <FormattedMessage {...messages.InvoiceRemark} />
                  {registration.relation && registration.relation.languageCode ? (
                    <span
                      title={intl.formatMessage(messages.RelationLanguage)}
                    >{` (${registration.relation.languageCode})`}</span>
                  ) : null}
                </span>
              }
              inputCols="col-sm-9"
              labelCols="col-sm-3"
            >
              <textarea
                id="RemarkField"
                className="form-control vertical-resize"
                value={registration.remark}
                disabled={invalidResource || disabled}
                onChange={e => this.doAction(actions.remarkChanged, e.target.value)}
                placeholder={intl.formatMessage(messages.InvoiceRemark)}
                maxLength="3000"
              />
            </FormGroupPart>
          </div>
        )}
        {options.showInternalRemark && (
          <div className="form-group">
            <FormGroupPart
              label={messages.InternalRemark}
              inputCols="col-sm-9"
              labelCols="col-sm-3"
            >
              <textarea
                id="InternalRemarkField"
                className="form-control vertical-resize"
                value={registration.internalRemark}
                disabled={invalidResource || disabled}
                onChange={e => this.doAction(actions.internalRemarkChanged, e.target.value)}
                placeholder={intl.formatMessage(messages.InternalRemark)}
                maxLength="3000"
              />
            </FormGroupPart>
          </div>
        )}
        {options.showInteraction && (
          <CanCreate on="InteractionsOwn">
            <InteractionFormGroup
              interactionType={registration.interactionType}
              interactionTitle={registration.interactionTitle}
              interactionDescription={registration.interactionDescription}
              doAction={this.doAction}
              disabled={invalidResource || disabled}
              intl={intl}
            />
          </CanCreate>
        )}
        {registration.type === 4 && overtimeRemaining ? (
          <div className="form-group">
            <FormGroupPart
              inputCols="col-sm-9"
              labelCols="col-sm-3"
              label={messages.RemainingOvertimeHours}
            >
              {overtimeRemaining.minuteToTime()}
            </FormGroupPart>
          </div>
        ) : null}
      </>
    );
  }
}

RegistrationComponent.propTypes = {
  registration: registrationShape.isRequired,
  onAction: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  intl: intlShape.isRequired,
  options: PropTypes.shape({}),
  showValidation: PropTypes.bool,
  // Select this resource on init (if available)
  defaultResourceId: PropTypes.string,
  // If the mainTaskId is provided, only this mainTask and its subtasks can be selected
  mainTaskId: PropTypes.string,
  overtimeRemaining: PropTypes.number,
  onPriceLoadingChanged: PropTypes.func,
};
RegistrationComponent.defaultProps = {
  disabled: false,
  options: {},
  showValidation: false,
  defaultResourceId: null,
  mainTaskId: null,
  overtimeRemaining: null,
  onPriceLoadingChanged: null,
};

export default injectIntl(RegistrationComponent);
